import {spotheroPost} from 'api';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {
    type CanonicalFacility,
    type FacilityFilterOptions,
    type FormattedCanonicalFacility,
    type FormattedParkingSpot,
} from 'api/facilities/facilities.types';
import {type ApiResponse, type RawApiResponse} from 'api/api.types';
import {type SelectInputOptions} from 'common/common.types';

type GetSearchedFacilitiesRequest = {
    filters?: {
        cities?: SelectInputOptions[];
        status?: SelectInputOptions[];
        operatorEmail?: string;
        employeeParkingEnabled?: boolean;
    };
    limit?: number;
    offset?: number;
    facilityIds?: number[] | string;
    searchText?: string;
};

type GetSearchedFacilitiesFormattedRequest = Omit<
    GetSearchedFacilitiesRequest,
    'filters'
> & {
    filters: FacilityFilterOptions;
    operatorEmail?: string;
};

type GetSearchedFacilitiesRawRequest =
    RawApiResponse<GetSearchedFacilitiesFormattedRequest>;

interface GetSearchedFacilitiesResponse {
    canonicalFacilities: CanonicalFacility[];
}

export type GetSearchedFacilitiesRawResponse = RawApiResponse<{
    results: GetSearchedFacilitiesResponse;
}>;

interface GetSearchedFacilitiesFormattedResponse {
    results: FormattedCanonicalFacility[];
    meta: {
        total: number;
        totalFacilityCount: number;
        totalFilteredFacilityCount: number;
        limit: number;
        offset: number;
        nextOffset: number;
    };
}

/**
 * Formats the request to get searched facilities.
 * @function formatGetSearchedFacilitiesRequest
 * @param request
 * @return - returns a formatted request object.
 *
 * @memberof module:api/facilities/dto
 */
export const formatGetSearchedFacilitiesRequest = (
    request: GetSearchedFacilitiesRequest
): GetSearchedFacilitiesRawRequest => {
    const formattedCities = request?.filters?.cities?.map((item) => {
        const city = item.value.split(',')[0].trim();
        const state = item.value.split(',')[1].trim();

        return {
            city,
            state,
        };
    });

    const formattedStatuses = request?.filters?.status?.map(
        (item) => item.value
    );

    const formattedRequestData: GetSearchedFacilitiesFormattedRequest = {
        ...request,
        limit: request?.limit || 25,
        offset: request?.offset || 0,
        filters: {
            cities: formattedCities,
            status: formattedStatuses,
            employeeParkingEnabled: request?.filters?.employeeParkingEnabled,
        },
        facilityIds: request?.facilityIds?.toString() || '',
        operatorEmail: request?.filters?.operatorEmail || '',
    };

    const formattedRequest =
        convertKeysToSnakeCase<GetSearchedFacilitiesFormattedRequest>(
            formattedRequestData
        );

    return formattedRequest;
};
/**
 * Format the response of searched facilities.
 *
 * @function formatGetSearchedFacilitiesResponse
 * @param data The data to be formatted.
 * @returns - The formatted response object.
 *
 * @memberof module:api/facilities/dto
 */
export const formatGetSearchedFacilitiesResponse = (
    data: ApiResponse<GetSearchedFacilitiesRawResponse>
): GetSearchedFacilitiesFormattedResponse => {
    const formattedResponse = camelizeKeys(data);

    const canonicalFacilities =
        formattedResponse?.data?.data?.results?.canonicalFacilities;

    const formattedCanonicalFacilities = canonicalFacilities?.reduce(
        (acc: FormattedCanonicalFacility[], facility) => {
            const formattedFacility: FormattedCanonicalFacility = {
                ...facility,
                parkingSpots:
                    facility?.parkingSpots?.map((parkingSpot) => {
                        const formattedParkingSpot: FormattedParkingSpot = {
                            ...parkingSpot,
                            address: facility?.address,
                        };

                        return formattedParkingSpot;
                    }) || [],
            };

            return [...acc, formattedFacility];
        },
        []
    );

    const response = {
        results: formattedCanonicalFacilities,
        meta: formattedResponse?.data?.meta,
    };

    return response;
};

/**
 * Retrieves searched facilities from the operator API based on the provided search parameters.
 *
 * @param params - The search parameters for retrieving facilities.
 * @returns - A promise that resolves with the formatted response containing the searched facilities.
 *
 * @see '/api/v1/facilities/operator-facility-search/'
 */
const getSearchedFacilities = async (
    params: GetSearchedFacilitiesRequest
): Promise<GetSearchedFacilitiesFormattedResponse> => {
    try {
        const formattedRequest = formatGetSearchedFacilitiesRequest(params);

        const response = await spotheroPost<GetSearchedFacilitiesRawResponse>(
            '/facilities/operator-facility-search/',
            formattedRequest,
            {},
            false
        );

        const formattedResponse = formatGetSearchedFacilitiesResponse(response);

        return formattedResponse;
    } catch (error) {
        throw Error(error);
    }
};

export default getSearchedFacilities;
